import { Button } from "@/components/ui/button";
import { SubscriptionStatusBanner } from "@/components/ui/SubscriptionStatusBanner";
import LoadingSpinner from "@/components/LoadingSpinner";
import { SubscriptionStatus } from "@/types/organization-usage";
import { useOrganizationSubscriptionData } from "@/providers/OrganizationSubscriptionProvider";

const BillingButtonContent = ({
  isLoading,
  canceledAt,
  isCanceled,
}: {
  isLoading: boolean;
  canceledAt: string | undefined;
  isCanceled: boolean;
}) => {
  if (isLoading) {
    return (
      <div className="flex items-center gap-2">
        <LoadingSpinner size={5} /> Loading...
      </div>
    );
  }

  if (canceledAt && !isCanceled) {
    return <>Renew Subscription</>;
  }

  return <>Manage Billing</>;
};

interface PlanActionsProps {
  isLoadingSubscriptionStatus: boolean;
  isCanceled: boolean;
  canceledAt: string | undefined;
  isScheduledPlanChange: boolean;
  isFreePlan: boolean;
  hasStripe: boolean;
  showPricingModal: boolean;
  setShowPricingModal: (show: boolean) => void;
  cancelSubscription: () => void;
  isLoadingCancelSubscription: boolean;
  handleManageBilling: () => void;
  isLoadingCreateCustomerPortal: boolean;
  subscriptionStatus?: SubscriptionStatus;
  isLegacy?: boolean;
  isNewEnterprisePlan?: boolean;
}

export const PlanActions = ({
  isLoadingSubscriptionStatus,
  isCanceled,
  canceledAt,
  isScheduledPlanChange,
  isFreePlan,
  hasStripe,
  showPricingModal,
  setShowPricingModal,
  cancelSubscription,
  isLoadingCancelSubscription,
  handleManageBilling,
  isLegacy,
  isLoadingCreateCustomerPortal,
  isNewEnterprisePlan,
}: PlanActionsProps) => {
  const { hasPaymentIssue } = useOrganizationSubscriptionData();

  return (
    <div className="ml-auto flex items-center gap-3">
      {isScheduledPlanChange && (
        <Button
          variant="destructiveOutline"
          size="sm"
          disabled={isLoadingCancelSubscription}
          onClick={() => cancelSubscription()}
        >
          {isLoadingCancelSubscription ? <LoadingSpinner size={5} /> : null}
          Cancel Subscription
        </Button>
      )}

      {!isNewEnterprisePlan &&
        !isLoadingSubscriptionStatus &&
        (isCanceled || !canceledAt) &&
        !isScheduledPlanChange &&
        !hasPaymentIssue && (
          <SubscriptionStatusBanner
            showPricingModal={showPricingModal}
            buttonText="Select a Plan"
            setShowPricingModal={setShowPricingModal}
            disabled={hasPaymentIssue}
          />
        )}

      {!isFreePlan && !isLoadingSubscriptionStatus && hasStripe && !isLegacy && (
        <Button
          variant="outline"
          size="sm"
          onClick={handleManageBilling}
          disabled={isLoadingCreateCustomerPortal}
          className="min-w-[120px] shadow-sm"
        >
          <BillingButtonContent
            isLoading={isLoadingCreateCustomerPortal}
            canceledAt={canceledAt}
            isCanceled={isCanceled}
          />
        </Button>
      )}
    </div>
  );
};
